// src/App.jsx
import React, { Suspense, lazy } from "react";
import { Provider } from "react-redux";
import { store } from "./store/store";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "./component/permission/ProtectedRoute";
import MultiAuthGate from "./component/auth/MultiAuthGate";
import { PermissionProvider } from "./provider/PermissionProvider";
import { Outlet } from "react-router-dom";
import PowerBIWrapper from "./component/permission/PowerBIWrapper";
import { ToastProvider } from "./provider/ToastProvider";
import Header from "./component/Header";
import { signOut } from "aws-amplify/auth";
import { FRONT_END_DOMAIN } from "./utils/LS-CONSTANT";
import {
    ADMIN_PERMISSION_EDIT,
    ADMIN_PERMISSION_VIEW,
    LS_DOCUMENTS_VIEW,
} from "./component/permission/permission-consts";
import { Box, ThemeProvider } from "@mui/material";
import theme from "./theme/theme";
import "./App.css";
import LsPageLoader from "./core/LsPageLoader";

/* -----------------------------
   Lazy loaded pages
------------------------------ */

const Home = lazy(() => import("./component/home/Home"));
const UsersPage = lazy(() => import("./component/access-management/users/UsersPage"));
const PermissionsPage = lazy(() => import("./component/access-management/permissions/PermissionsPage"));
const RolesPage = lazy(() => import("./component/access-management/roles/RolesPage"));
const DocumentsPage = lazy(() => import("./component/document/DocumentsPage"));
const AccountPage = lazy(() => import("./component/account/AccountPage"));

/* -----------------------------
   Layout
------------------------------ */

const Layout = () => {
    const handleSignOut = async () => {
        try {
            document.cookie =
                "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure";
            await signOut();
            window.location.href = `https://${FRONT_END_DOMAIN}`;
        } catch (error) {
            console.error("Error during sign out:", error);
        }
    };

    return (
        <Box sx={{ display: "flex" }}>
            <Header onSignOut={handleSignOut} />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    marginLeft: `0`,
                    marginTop: "64px",
                    transition: "margin-left 0.3s",
                    minHeight: "calc(100vh - 64px)",
                    backgroundColor: "#f8fafc",
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
};

function App() {
    return (
        <Provider store={store}>
            <ThemeProvider theme={theme}>
                <ToastProvider>
                    <PermissionProvider>
                        <BrowserRouter>
                            <MultiAuthGate>
                                <Suspense fallback={<LsPageLoader />}>
                                    <Routes>
                                        <Route path="/" element={<Layout />}>

                                            {/* Default → /dashboard */}
                                            <Route
                                                index
                                                element={<Navigate to="/dashboard" replace />}
                                            />

                                            {/* /home → /dashboard */}
                                            <Route
                                                path="/home"
                                                element={<Navigate to="/dashboard" replace />}
                                            />

                                            {/* Dashboard */}
                                            <Route
                                                path="/dashboard"
                                                element={
                                                    <ProtectedRoute>
                                                        <Home />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/powerbi/:report"
                                                element={<PowerBIWrapper />}
                                            />

                                            <Route
                                                path="/access-control/users"
                                                element={
                                                    <ProtectedRoute
                                                        anyPermissions={[
                                                            ADMIN_PERMISSION_VIEW,
                                                            ADMIN_PERMISSION_EDIT,
                                                        ]}
                                                    >
                                                        <UsersPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/access-control/permissions"
                                                element={
                                                    <ProtectedRoute
                                                        anyPermissions={[
                                                            ADMIN_PERMISSION_VIEW,
                                                            ADMIN_PERMISSION_EDIT,
                                                        ]}
                                                    >
                                                        <PermissionsPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/access-control/roles"
                                                element={
                                                    <ProtectedRoute
                                                        anyPermissions={[
                                                            ADMIN_PERMISSION_VIEW,
                                                            ADMIN_PERMISSION_EDIT,
                                                        ]}
                                                    >
                                                        <RolesPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/document/*"
                                                element={
                                                    <ProtectedRoute
                                                        anyPermissions={[LS_DOCUMENTS_VIEW]}
                                                    >
                                                        <DocumentsPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/accounts"
                                                element={
                                                    <ProtectedRoute
                                                        anyPermissions={[LS_DOCUMENTS_VIEW]}
                                                    >
                                                        <AccountPage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                        </Route>

                                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                                    </Routes>
                                </Suspense>
                            </MultiAuthGate>
                        </BrowserRouter>
                    </PermissionProvider>
                </ToastProvider>
            </ThemeProvider>
        </Provider>
    );
}

export default App;